import { Box, Typography, Button, Avatar } from '@mui/material';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'next-i18next';

type ConsultantPopupProps = {
    lat: number;
    lng: number;
    zIndex?: number; // consumed by google-maps-react-markers OverlayView
    consultant: {
        id: number;
        first_name: string;
        last_name: string;
        shipping_city: string;
        shipping_postal_code: string;
        profile_photo_path?: string | null;
        profile_url: string;
    };
    onClose: () => void;
    onViewProfile: (profileUrl: string) => void;
};

export function ConsultantPopup({ lat, lng, consultant, onClose, onViewProfile }: ConsultantPopupProps) {
    const { t } = useTranslation('common');
    const popupRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose]);

    useEffect(() => {
        const node = popupRef.current?.parentElement as HTMLElement | null;
        if (node) {
            node.style.zIndex = '100000';
            node.style.pointerEvents = 'auto';
        }
    }, [lat, lng]);

    useEffect(() => {
        const panes = document.getElementsByClassName('google-map-markers-overlay');
        for (let i = 0; i < panes.length; i += 1) {
            const el = panes[i] as HTMLElement;
            el.style.zIndex = '100000';
            el.style.pointerEvents = 'auto';
        }
    }, [lat, lng]);

    return (
        <Box
            ref={popupRef}
            sx={{
                position: 'relative',
                left: -100,
                top: -120,
                zIndex: 9999,
                backgroundColor: '#1a1a1a',
                borderRadius: 1,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                p: 1.5,
                minWidth: 200,
                maxWidth: 240,
                border: '1px solid #333',
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 0,
                    height: 0,
                    borderLeft: '8px solid transparent',
                    borderRight: '8px solid transparent',
                    borderTop: '8px solid #1a1a1a',
                },
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Avatar
                    src={consultant.profile_photo_path || undefined}
                    sx={{
                        width: 36,
                        height: 36,
                        mr: 1.5,
                        backgroundColor: '#333',
                        color: '#fff',
                        fontSize: '12px',
                        fontWeight: 600,
                    }}
                >
                    {consultant.first_name[0]}
                    {consultant.last_name[0]}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                    <Typography
                        variant="subtitle2"
                        sx={{
                            fontWeight: 600,
                            color: '#fff',
                            fontSize: '14px',
                            lineHeight: 1.2,
                            mb: 0.5,
                        }}
                    >
                        {consultant.first_name}
                        {' '}
                        {consultant.last_name}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            color: '#ccc',
                            fontSize: '12px',
                            lineHeight: 1.2,
                        }}
                    >
                        {consultant.shipping_postal_code}
                        ,
                        {consultant.shipping_city}
                    </Typography>
                </Box>
                <Button
                    size="small"
                    onClick={onClose}
                    sx={{
                        minWidth: 'auto',
                        p: 0.5,
                        color: '#999',
                        '&:hover': {
                            color: '#fff',
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        },
                    }}
                >
                    ×
                </Button>
            </Box>
            <Button
                variant="contained"
                size="small"
                fullWidth
                onClick={() => onViewProfile(consultant.profile_url)}
                sx={{
                    backgroundColor: '#e91e63',
                    color: '#fff',
                    fontSize: '12px',
                    fontWeight: 500,
                    py: 0.8,
                    '&:hover': {
                        backgroundColor: '#c2185b',
                    },
                }}
            >
                {t('find-consultant.view-profile')}
            </Button>
        </Box>
    );
}
